import React, { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { Card, CardBody, CardExpandToggler } from '../other/card/card';
import { useGetPerPlatformMonthDetectedReportedRemovedQuery } from '../../redux/Api/AnalyticsApiSlice';
import ComponentLoader from '../other/ComponentLoader';
import { toast } from 'react-toastify';

const PerServiceStats = () => {
    const { data: perPlatformMonthDetectedReportedRemovedCampaigns, isLoading, error, isFetching } = useGetPerPlatformMonthDetectedReportedRemovedQuery({ campaign_id: "" })

    useEffect(() => {
        if (error?.data) {
            const errorMessages = Object.values(error.data).flat()
            errorMessages.forEach(message => {
                toast.error(message)
            })
        }
    }, [error])
    const [type, setType] = useState("detected");
    const [detectedSeries, setDetectedSeries] = useState([]);
    const [removedSeries, setRemovedSeries] = useState([]);
    const [dataCategories, setDataCategories] = useState([]);

    const colorMap = {
        Twitch: "#6441A4",
        Snapchat: "#FFFC00",
        Facebook: '#3081D0',
        Instagram: '#FFC47E',
        Twitter: '#000000',
        Youtube: '#C70039',
        Tiktok: "#69C9D0",
    };

    function getAxisTextColor() {
        const appMode = localStorage.getItem('appMode'); // Get appMode class from localStorage
        const axisColor = appMode === 'dark' ? '#ffffff' : '#000000'; // White text for dark mode, black text for light mode
        return axisColor || '#000000'; // Fallback to black if color is invalid
    }

    const getStyles = () => {
        const themeFont = getComputedStyle(document.body).getPropertyValue('--bs-body-font-family').trim() || 'Arial, sans-serif';;
        const themeFontWeight = getComputedStyle(document.body).getPropertyValue('--bs-body-font-weight').trim();
        const gray600 = getComputedStyle(document.body).getPropertyValue('--bs-gray-600').trim();
        const pink = getComputedStyle(document.body).getPropertyValue('--bs-pink').trim();
        const inverse = getComputedStyle(document.body).getPropertyValue('--bs-inverse').trim();
        const inverseRgb = getComputedStyle(document.body).getPropertyValue('--bs-inverse-rgb').trim();
        const borderColor = getComputedStyle(document.body).getPropertyValue('--bs-border-color').trim() || '#ddd';

        return { themeFont, themeFontWeight, gray600, pink, inverse, inverseRgb, borderColor };
    };

    const { themeFont, themeFontWeight, gray600, pink, inverse, inverseRgb, borderColor } = getStyles();

    const areaChartOptions = {
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 3 },
        colors: [pink, gray600],
        grid: { borderColor:"#ddd" },
        yaxis: {
            labels: { style: { colors: getAxisTextColor(),  fontSize: '12px', fontFamily: themeFont, fontWeight: themeFontWeight } }
        },
        legend: { fontFamily: themeFont, labels: { colors: inverse } },
        tooltip: { x: { format: 'dd/MM/yy HH:mm' } },
        xaxis: {
            type: "date",
            categories: dataCategories,
            axisTicks: { show: true, borderType: 'solid', color: `rgba(${inverseRgb}, .25)`, height: 6 },
            axisBorder: { show: true, color: `rgba(${inverseRgb}, .25)`, height: 1 },
            labels: { style: { colors: getAxisTextColor(),  fontSize: '12px', fontFamily: themeFont, fontWeight: themeFontWeight } },
        },
        chart: {
            toolbar: {
                show: false // Disable the toolbar
            }
        },
    };

    const convertDateToString = (date) => {
        const year = date.slice(0, 4);
        const month = date.slice(5, 7);
        const monthName = new Date(year, month - 1).toLocaleString('en-us', { month: 'long' }).slice(0, 3);
        const yearShort = year.slice(2, 4);
        return `${monthName} ${yearShort}`;
    };

    const generateData = (data) => {
        if (!data) return;

        const tmpCategories = [];
        const tmpDetectedSeries = [];
        const tmpRemovedSeries = [];

        Object.entries(data).forEach(([service, months]) => {
            if (service === 'overall') return;

            const detectedSeriesData = [];
            const removedSeriesData = [];

            Object.entries(months).forEach(([date, statistics]) => {
                if (date === 'overall') return;

                tmpCategories.push(convertDateToString(date));
                Object.entries(statistics).forEach(([type, total_links]) => {
                    if (type === 'reported') detectedSeriesData.push(total_links);
                    if (type === 'removed') removedSeriesData.push(total_links);
                });
            });

            tmpDetectedSeries.push({ name: service, data: detectedSeriesData, color: colorMap[service] });
            tmpRemovedSeries.push({ name: service, data: removedSeriesData, color: colorMap[service] });
        });

        setDataCategories(tmpCategories);
        setDetectedSeries(tmpDetectedSeries);
        setRemovedSeries(tmpRemovedSeries);
    };

    useEffect(() => {
        generateData(perPlatformMonthDetectedReportedRemovedCampaigns);
    }, [perPlatformMonthDetectedReportedRemovedCampaigns]);

    const series = type === 'detected' ? detectedSeries : removedSeries;
    return (
        <div>
            <Card className="mb-3">
                <CardBody className="relative p-0 min-h-20">
                    <div className='p-0'>
                        {isLoading ? <ComponentLoader /> : null}
                        <div className='p-4'>
                            <div className="d-flex justify-content-between mb-3">
                                <span className="fw-bold small">Platform Insights</span>
                                <CardExpandToggler />
                            </div>
                            {series.length > 0 ?
                                <>
                                    <div className="mb-3 flex gap-3">
                                        <button
                                            className={`btn ${type === 'detected' ? 'btn-outline-theme' : 'btn-outline-secondary'}`}
                                            onClick={() => setType('detected')}
                                        >
                                            Reported
                                        </button>
                                        <button
                                            className={`btn ${type === 'removed' ? 'btn-outline-theme' : 'btn-outline-secondary'}`}
                                            onClick={() => setType('removed')}
                                        >
                                            Removed
                                        </button>
                                    </div>
                                    <div className="mb-3 h-full">
                                        <Chart type="area" height='100%' options={areaChartOptions} series={series} />
                                    </div>
                                </>
                                :
                                <div className="mb-3 h-full">

                                </div>
                            }
                        </div>
                    </div>
                </CardBody>
            </Card>
        </div>
    );
};

export default PerServiceStats;
